import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheckBig, Clock, Plus, Receipt, Wallet } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import { Select } from '../../components/Controls';
import { Kpi } from '../attendance/shared';
import { active, COMMITTED, DetailModal, go, HBar, Header, naira, REVIEW, Status, statusCounts, StatusPie, sum, TrendBar, TypeBar } from './shared';
import Loader from '../../components/Loader';

// HR Expenses > Dashboard (Stephanet's hrexpenses/Dashboard.jsx): totals, approvals, charts and the latest expenses
export default function HrExpenseDashboard() {
  const { notify } = useHR();
  const [year, setYear] = useState('');
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const load = () => hr.hreList({}).then(setD).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => (d ? active(d.rows).filter(r => !year || String(r.year) === year) : []), [d, year]);
  const years = useMemo(() => (d ? [...new Set(d.rows.map(r => r.year).filter(Boolean))].sort() : []), [d]);
  if (error) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading HR expenses…" />;

  const committed = rows.filter(r => COMMITTED.includes(r.status));
  const pending = rows.filter(r => REVIEW.includes(r.status));
  const mine = rows.filter(r => r.awaits_me);
  const recent = [...rows].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 10);

  return (
    <div className="mdr mds ael hre">
      <Header />
      <div className="hre-bar no-print">
        <label className="hre-year">Year:
          <Select value={year} onChange={e => setYear(e.target.value)}><option value="">All Years</option>{years.map(y => <option key={y} value={String(y)}>{y}</option>)}</Select>
        </label>
        {d.can.create && <Link className="mdr-btn" to={go('hre-add')}><Plus size={14} /> Add Expense</Link>}
      </div>

      <div className="md-kpis mds-kpis hre-kpis">
        <Kpi label="Total HR Expenses" value={naira(sum(rows))} Ico={Receipt} tone="blue" note={`${rows.length} records`} />
        <Kpi label="Approved" value={naira(sum(committed))} Ico={CircleCheckBig} tone="green" note={`${committed.length} approved`} i={1} />
        <Kpi label="Pending Review" value={pending.length} Ico={Clock} tone="amber" note={`Awaiting action · ${naira(sum(pending))}`} i={2} />
        <Kpi label="Waiting for You" value={mine.length} Ico={Wallet} tone="teal" note={mine.length ? naira(sum(mine)) : 'Nothing at your step'} i={3} />
      </div>

      <div className="hre-chips">
        {statusCounts(rows).map(x => <Link key={x.name} to={go('hre-manage', { status: x.name })} className="hre-chip" style={{ '--c': x.color }}>{x.name}: <b>{x.value}</b></Link>)}
      </div>

      <div className="hre-grid2">
        <TypeBar rows={rows} />
        <StatusPie rows={rows} />
        <HBar rows={rows} by="department" title="Expenses by Department" />
        <TrendBar rows={rows} year={year} />
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="hre-panel-h hre-pad"><h3>Recent HR Expenses</h3><span>Latest 10</span></div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table hre-table">
            <thead><tr><th>Expense ID</th><th>Expense Name</th><th>Type</th><th>Employee</th><th>Department</th><th>Project Site</th><th className="r">Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {recent.map(r => (
                <tr key={r.id} className="mdr-row clickable" onClick={() => setViewing(r.id)}>
                  <td className="mono nowrap"><b>{r.reference}</b></td><td className="nowrap">{r.title}</td><td className="nowrap">{r.expense_type || '—'}</td>
                  <td className="nowrap">{r.employee || '—'}</td><td className="nowrap">{r.department || '—'}</td><td className="nowrap">{r.site || '—'}</td>
                  <td className="r mono nowrap">{naira(r.amount)}</td><td><Status s={r.status} /></td><td className="nowrap">{r.date || '—'}</td>
                </tr>
              ))}
              {!recent.length && <tr><td colSpan={9} className="mdr-empty">No HR expenses yet. Use Add Expense to record the first one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {viewing && <DetailModal id={viewing} notify={notify} onClose={() => setViewing(null)} onChanged={load} />}
    </div>
  );
}
